import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import classnames from "classnames";
import { FieldError } from "react-hook-form";

import { useWombatTheme } from "../../../theme";
import useCombinedRef from "../../../utils/useCombinedRefs";

export type CheckboxVariant = "base" | "boxed";

type CheckboxProps = {
  /**
   * Label
   */
  label: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Input id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: checkbox_${id}_group
   *  - Label: checkbox_${id}_label
   *  - Input: checkbox_${id}
   *  - Help text: checkbox_${id}_help_message
   *  - Error message: checkbox_${id}_error_message
   *
   * It will also do the matching from the label to the input in respect to accessibility patterns
   */
  id: string;
  /**
   * Helper text
   * @default undefined
   */
  help?: string;
  /**
   * Variant
   * @default 'base'
   */
  variant?: CheckboxVariant;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Uses the icon instead of the checkbox
   * @note The input isn't remove, just hidden
   * @default null
   */
  checkedIcon?: React.ReactElement;
  /**
   * Uses the icon instead of the checkbox
   * @note The input isn't remove, just hidden
   * @default null
   */
  uncheckedIcon?: React.ReactElement;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
} & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "type"
>;

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      help,
      error = {},
      id,
      srOnlyLabel,
      checkedIcon: CheckedIcon = null,
      uncheckedIcon: UncheckedIcon = null,
      variant = "base",
      disabled = false,
      onChange,
      className,
      ...props
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const innerRef = useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRef(ref, innerRef);
    const [variantStyleStatus, setVariantStyleStatus] = useState(
      classnames(
        theme.form.checkbox.variant.container[variant],
        theme.form.checkbox.variant.state[variant][
          props.defaultChecked ? "checked" : "unchecked"
        ]
      )
    );

    const _onChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setVariantStyleStatus(
          classnames(
            theme.form.checkbox.variant.container[variant],
            theme.form.checkbox.variant.state[variant][
              combinedRef?.current?.checked ? "checked" : "unchecked"
            ]
          )
        );
        if (onChange) onChange(e);
      },
      [onChange, variant]
    );

    const checkboxInputVariantStyle = classnames(
      theme.form.checkbox.variant.input[variant]
    );
    const checkboxStyle = classnames(
      theme.form.checkbox.base,
      "cursor-pointer",
      theme.form.checkbox.status[error?.message ? "error" : "default"],
      CheckedIcon && UncheckedIcon && "hidden",
      className
    );
    const labelStyle = classnames(
      theme.form.checkbox.label.base,
      "cursor-pointer",
      disabled
        ? theme.form.checkbox.label.disabled
        : theme.form.checkbox.label.able,
      srOnlyLabel && "sr-only"
    );
    const helpStyle = classnames(
      theme.form.checkbox.help[error?.message ? "error" : "default"],
      srOnlyLabel && "sr-only"
    );

    const icon = useMemo(() => {
      if (CheckedIcon && UncheckedIcon) {
        if (combinedRef?.current?.checked) return CheckedIcon;
        else return UncheckedIcon;
      }
      return null;
    }, [combinedRef?.current?.checked]);

    return (
      <div className={variantStyleStatus} data-test-id={`checkbox_${id}_group`}>
        <div className={checkboxInputVariantStyle}>
          <input
            id={id}
            ref={combinedRef}
            disabled={disabled}
            aria-disabled={disabled ? "true" : "false"}
            type="checkbox"
            className={checkboxStyle}
            data-test-id={`checkbox_${id}`}
            onChange={_onChange}
            {...props}
          />
          {CheckedIcon && UncheckedIcon && (
            <label htmlFor={id} data-test-id={`checkbox_icon_${id}_label`}>
              {icon}
            </label>
          )}
        </div>
        <label
          htmlFor={id}
          className={labelStyle}
          data-test-id={`checkbox_${id}_label`}
        >
          <div className={`ml-3 text-sm ${srOnlyLabel ? "sr-only" : ""}`}>
            {label}
            {(help || error?.message) && (
              <p
                className={helpStyle}
                data-test-id={`checkbox_${id}_${
                  error?.message ? "error_message" : "help_message"
                }`}
              >
                {error?.message ? error?.message : help}
              </p>
            )}
          </div>
        </label>
      </div>
    );
  }
);

export default React.memo(Checkbox);
